/**
 * ForecastCards.tsx — Forecast programme F4a (register row 72): four cards of
 * the Forecast tab, rendered from forecastCards.ts's models and the wire, spec
 * §3.4–§3.6 under ADOPTION §18 (the rules are stated in forecastCards.ts).
 *
 *   - CategorisedShareCard — the ruled name (§18.4 item 2), the level word
 *     beside the share, the 8px meter in the level's status colour, the
 *     guidance sentence.
 *   - AttributionCard — CO-EQUAL and ALWAYS present (§18.5; hide-at-zero
 *     REFUSED). The pre-F4 panel's content is all kept (the explanation, the
 *     verdict badge and sentence, both halves with their fence counts); F4a
 *     adds the icon tile, the headline and the split bar with its captions.
 *     "Link owners ›" is NOT here: it is F4b (register row 81), which waits on
 *     migration 0132.
 *   - LookAheadCard — the wire's NEXT period, never a hardcoded "Q4". An empty
 *     next period renders a statement about that period (from 1 October this
 *     is what Fourth Coffee's default view shows: Q1 2027 has no open deal).
 *   - ByCategoryCard — the roll-up re-skinned (dot + name, value, count, a
 *     6px bar of the share of open value) on the validated chart slots; the
 *     pre-F4 content (coverage line, effective/rep views, overridden deals,
 *     weighted figures) is kept.
 */
import { useState } from 'react'
import { Info, Sparkles } from 'lucide-react'
import type { SalesForecast } from '../../../../api/types'
import { LinkOwnersModal } from './LinkOwnersModal'
import { useWriteAccess } from '../../../../lib/writeAccess'
import { money } from '../../../../lib/money'
import { success, warning, danger, info, forecastAttributionSplit, forecastLookAheadBar } from '../../../../theme/tokens'
import {
  categorisedShare, attributionSplit, lookAhead, byCategoryHead, shareOfOpen, LEVEL_WORD, type ShareLevel,
} from './forecastCards'
import {
  attributionSentence, verdictSentence, verdictBadge, fencedDealCount, rollupBars, overrideCoverageSentence, gapChip,
} from './forecastView'

const LEVEL_TONE: Record<ShareLevel, string> = { high: success.text, medium: warning.text, low: danger.text }

export function CategorisedShareCard({ data: f }: { data: SalesForecast }) {
  const m = categorisedShare(f)
  const tone = m.level ? LEVEL_TONE[m.level] : undefined
  return (
    <section className="k-card" aria-label="Categorised share" data-share-level={m.level ?? 'none'}>
      <div className="p-5 flex flex-col gap-3">
        <div className="text-13 font-semibold text-ink-primary">Categorised share</div>
        <div className="flex items-baseline gap-3">
          {m.level && (
            <span style={{ fontSize: 34, fontWeight: 700, lineHeight: 1, color: tone }} data-level-word>{LEVEL_WORD[m.level]}</span>
          )}
          <span className="text-13 text-ink-body" data-share-line>{m.line}</span>
        </div>
        {m.sharePct !== null && (
          <div className="h-2 rounded-full bg-surface-control overflow-hidden" role="meter" aria-label="Categorised share of open value"
            aria-valuemin={0} aria-valuemax={100} aria-valuenow={m.sharePct}>
            <div className="h-full rounded-full" style={{ width: `${m.sharePct}%`, background: tone }} />
          </div>
        )}
        <div className="text-12 text-ink-muted" data-share-figures>{m.figures}</div>
        <p className="text-13 text-ink-body" data-share-guidance>{m.guidance}</p>
      </div>
    </section>
  )
}

export function AttributionCard({ data: f, onChanged }: { data: SalesForecast; onChanged?: () => void }) {
  const m = attributionSplit(f)
  // F4b (row 81): the link exists only while the wire lists a deal to link —
  // a pre-F4b response, or a period with none, renders no link.
  const [linking, setLinking] = useState(false)
  // Linking an owner is a deal write: records-level (lib/writeAccess).
  const { records } = useWriteAccess()
  const linkable = records && (f.unattributedWon?.items.length ?? 0) > 0
  const verdictTone = f.attainmentMeasurable
    ? { background: success.bg, color: success.text }
    : { background: warning.bg, color: warning.text }
  const tile = m.attention ? { background: warning.bg, color: warning.text } : { background: info.bg, color: info.text }
  return (
    <section className="k-card" aria-label="Attribution" data-attention={m.attention}>
      <div className="p-5 flex flex-col gap-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center rounded-control" style={{ width: 28, height: 28, ...tile }} aria-hidden="true">
              <Info size={15} />
            </span>
            <span className="text-13 font-semibold text-ink-primary">Attribution</span>
          </div>
          <span className="k-badge" style={verdictTone} data-measurable={f.attainmentMeasurable}>
            <span className="k-badge-dot" />{verdictBadge(f.attainmentMeasurable)}
          </span>
        </div>
        <div className="text-section-title font-semibold text-ink-primary leading-tight" data-attribution-headline>{m.headline}</div>
        <div className="text-12 text-ink-muted">
          Won value counts against a target only when its deal is linked to an employee. A won deal owned by a name alone counts for nobody's target.
        </div>
        <div className="flex h-2 gap-[2px]" role="img" aria-label={`${m.attributedCaption}; ${m.unattributedCaption}`} data-split>
          {m.attributedPct + m.unattributedPct === 0
            ? <div className="flex-1 rounded-full bg-surface-control" />
            : <>
                {m.attributedPct > 0 && <div className="rounded-full" data-split-part="attributed" style={{ width: `${m.attributedPct}%`, background: forecastAttributionSplit.attributed }} />}
                {m.unattributedPct > 0 && <div className="rounded-full" data-split-part="unattributed" style={{ width: `${m.unattributedPct}%`, background: forecastAttributionSplit.unattributed }} />}
              </>}
        </div>
        <div className="flex justify-between gap-3 text-12">
          <span className="text-ink-body" data-split-caption="attributed">{m.attributedCaption}</span>
          <span className="text-ink-body text-right" data-split-caption="unattributed">{m.unattributedCaption}</span>
        </div>
        <p className="text-13 text-ink-body">
          {attributionSentence(f)}{' '}
          <span className="font-semibold text-ink-primary">{verdictSentence(f.attainmentMeasurable)}</span>
        </p>
        {linkable && (
          <button type="button" className="self-start text-13 font-medium text-accent hover:underline" onClick={() => setLinking(true)}>
            Link owners ›
          </button>
        )}
      </div>
      {linking && records && <LinkOwnersModal data={f} onClose={() => setLinking(false)} onChanged={onChanged} />}
    </section>
  )
}

export function LookAheadCard({ data: f }: { data: SalesForecast }) {
  const m = lookAhead(f)
  if (!m) return null
  return (
    <section className="k-card" aria-label="Look-ahead" data-look-ahead={m.empty ? 'empty' : 'populated'} data-next-period={f.nextPeriod!.periodStart}>
      <div className="k-card-head">
        <div>
          <div className="k-card-title" data-look-ahead-title>{m.title}</div>
          <div className="k-card-sub">{m.subtitle}</div>
        </div>
        <div className="text-right">
          <div className="text-ink-primary" style={{ fontSize: 22, fontWeight: 600 }} title={m.totalExact}>{m.total}</div>
          <div className="text-12 text-ink-muted" data-look-ahead-due>{m.due}</div>
        </div>
      </div>
      <div className="p-5 flex flex-col gap-4">
        {m.empty ? (
          <p className="text-13 text-ink-body" data-look-ahead-empty>{m.emptyStatement}</p>
        ) : (
          <div className="flex gap-3" data-chart="look-ahead">
            <div className="relative flex-shrink-0 text-11 text-ink-muted" style={{ width: 48, height: 180 }} aria-hidden="true">
              {m.ticks.map(t => (
                <span key={t.value} className="absolute right-0" style={{ bottom: `${t.pct}%`, transform: 'translateY(50%)' }}>{t.label}</span>
              ))}
            </div>
            <div className="flex-1 min-w-0">
              <div className="relative" style={{ height: 180 }}>
                {m.ticks.map(t => (
                  <div key={t.value} className="absolute left-0 right-0 border-t border-edge-row" style={{ bottom: `${t.pct}%` }} aria-hidden="true" />
                ))}
                <div className="absolute inset-0 flex items-end gap-6 px-4">
                  {m.bars.map(b => (
                    <div key={b.month} className="flex-1 h-full flex flex-col justify-end items-center" data-month={b.month} data-month-empty={b.empty}>
                      <span className="text-13 font-semibold text-ink-primary mb-1">{b.valueLabel}</span>
                      <div className="w-full" title={b.exact} role="img" aria-label={b.exact}
                        style={{ height: `${b.heightPct}%`, minHeight: b.pence > 0 ? 2 : 0, background: forecastLookAheadBar, borderRadius: '8px 8px 0 0', maxWidth: 96 }} />
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex gap-6 px-4 mt-2">
                {m.bars.map(b => (
                  <div key={b.month} className="flex-1 min-w-0 text-12 text-ink-secondary text-center truncate" title={b.xLabel} data-month-label={b.month}>{b.xLabel}</div>
                ))}
              </div>
            </div>
          </div>
        )}
        {m.insight && (
          <div className="flex items-start gap-2 rounded-control bg-surface-panel px-3 py-2 text-13 text-ink-body" data-look-ahead-insight>
            <Sparkles size={14} className="mt-[2px] flex-shrink-0" aria-hidden="true" />{m.insight}
          </div>
        )}
        {m.fenceNote && <p className="text-12 text-ink-muted">{m.fenceNote}</p>}
        {m.footnote && <p className="text-12 text-ink-muted" data-look-ahead-footnote>{m.footnote}</p>}
      </div>
    </section>
  )
}

export function ByCategoryCard({ data: f }: { data: SalesForecast }) {
  const head = byCategoryHead(f)
  const bars = rollupBars(f.rollup)
  const repBars = rollupBars(f.repRollup)
  const coverage = overrideCoverageSentence(f.overrideCoverage)
  return (
    <section className="k-card" aria-label="By forecast category">
      <div className="k-card-head">
        <div>
          <div className="k-card-title">By forecast category</div>
          <div className="k-card-sub" title={head.totalExact}>{head.subtitle}</div>
        </div>
      </div>
      <div className="px-5 pb-1 text-12 text-ink-muted">Every open deal in the workspace as it stands today, by effective category — whoever owns it</div>
      {/* 2.3c: the coverage line, above the rows so the reader knows WHOSE view they are. */}
      {coverage && (
        <div className="px-5 py-2 text-12 text-ink-body border-b border-edge-row" data-all-overridden={f.overrideCoverage.allOverridden}>
          {coverage}
        </div>
      )}
      <div className="p-5 flex flex-col gap-4" data-view="effective">
        {f.overrideCoverage.allOverridden && (
          <div className="text-11 font-semibold uppercase text-ink-muted">Effective view — the override view</div>
        )}
        {bars.map(b => <CategoryRow key={b.category} bar={b} share={shareOfOpen(b.pence, f)} />)}
      </div>
      {f.overrideCoverage.allOverridden && (
        <section className="p-5 pt-0 flex flex-col gap-4 border-t border-edge-row" data-view="rep" aria-label="Rep view">
          <div className="text-11 font-semibold uppercase text-ink-muted mt-4">Rep view — the deals' own categories and values</div>
          {repBars.map(b => <CategoryRow key={b.category} bar={b} share={shareOfOpenOf(b.pence, repBars)} />)}
        </section>
      )}
      {f.overrides.length > 0 && (
        <ul className="border-t border-edge-row divide-y divide-edge-row" aria-label="Overridden deals">
          {f.overrides.map(o => (
            <li key={o.dealId} className="px-5 py-2 flex items-center justify-between gap-4 text-12" data-deal-id={o.dealId}>
              <span className="text-ink-primary font-medium truncate">
                {o.title}{o.employeeName ? <span className="text-ink-muted font-normal"> · {o.employeeName}</span> : null}
              </span>
              <span className="k-badge flex-shrink-0" data-gap-chip>{gapChip(o.repCategory, o.overrideCategory, o.gapPence, o.amountPence !== null)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function shareOfOpenOf(pence: number, bars: { pence: number }[]): number {
  const total = bars.reduce((a, b) => a + b.pence, 0)
  return total > 0 ? (pence / total) * 100 : 0
}

function CategoryRow({ bar: b, share }: { bar: ReturnType<typeof rollupBars>[number]; share: number }) {
  return (
    <div className="flex flex-col gap-1" data-category={b.category}>
      <div className="flex items-center gap-2">
        <span className="inline-block rounded-full flex-shrink-0" style={{ width: 8, height: 8, background: b.fill }} aria-hidden="true" />
        <span className="text-13 text-ink-body flex-1 min-w-0 truncate">{b.label}</span>
        <span className="text-13 font-semibold text-ink-primary">{money(b.pence / 100)}</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex-1 rounded-full bg-surface-control overflow-hidden" style={{ height: 6 }}>
          <div className="h-full rounded-full" style={{ width: `${share}%`, background: b.fill }} />
        </div>
        <span className="text-12 text-ink-muted whitespace-nowrap">{fencedDealCount(b.count, b.excludedNonGbp)}</span>
        <span className="text-12 text-ink-muted whitespace-nowrap">{money(b.weightedPence / 100)} weighted</span>
      </div>
    </div>
  )
}
